import type { CSSProperties } from 'react';
import { cx } from './cx';
import styles from './volume.module.css';

/**
 * The level slider both players draw — the watch page's chrome and the minimized player's.
 *
 * It exists as a component rather than as two `<input type="range">`s because three details are easy
 * to get subtly wrong and invisible when they are: `data-role="volume"` is what §7's keymap reads to
 * tell this control apart from the scrub bar (`keys.ts` → `keyTargetOf`), the filled part of the
 * track is painted from a custom property because neither engine's track pseudo-element can be
 * styled inline, and *muted* has to read as zero here while the remembered level stays untouched —
 * which is a decision the caller makes and this element states.
 *
 * Sizing is the caller's: the chrome collapses it until the group is hovered, the mini player keeps
 * it open. Both pass a `className`, and the declarations never overlap with this file's.
 */
export function VolumeSlider({
  level,
  onChange,
  label,
  className,
}: {
  /** 0–1, already resolved against mute by the caller — the bar states what is *audible*. */
  readonly level: number;
  readonly onChange: (level: number) => void;
  readonly label: string;
  readonly className?: string;
}) {
  return (
    <input
      className={cx(styles.volume, className)}
      data-role="volume"
      type="range"
      min={0}
      max={1}
      step={0.01}
      value={level}
      style={{ '--volume-level': `${String(level * 100)}%` } as CSSProperties}
      onChange={(event) => onChange(Number(event.target.value))}
      aria-label={label}
    />
  );
}
